import Axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import queryString from 'query-string';
import { DeepPartial } from 'redux';
import { call, cancelled, put } from 'redux-saga/effects';
import { AsyncActionCreator } from 'typesafe-actions';

import { Result } from '../reducers/createResultReducer';
import { ResourceLite } from '../resources/createResource';
import { EntityBase } from '../resources/types';
import axiosClientService from '../services/axiosClientService';
import { State } from '../types';

import sagaHandleApiError, { MetaHandleApiError } from './sagaHandleApiError';
import sagaHandleApiResponse, { MetaHandleApiResponse } from './sagaHandleApiResponse';
import updateLoading from './updateLoading';

export interface Meta<E> extends MetaHandleApiResponse<E>, MetaHandleApiError {
  axiosClientName?: string;
  showLoading?: boolean;
}

export interface PayloadApiSuccess<E> extends AxiosResponse<any> {
  normalized: {
    entities: DeepPartial<State['entities']>;
    result?: Result;
  };
  meta?: Meta<E>;
}

export interface PayloadApiFailure {
  error: AxiosError;
  meta: Meta<any>;
}

type AsyncAction<E extends EntityBase, P extends PayloadApiRequest<E>> = AsyncActionCreator<
  [string, P], // REQUEST
  [string, PayloadApiSuccess<E>], // SUCCESS
  [string, PayloadApiFailure], // FAILURE
  [string, any] // CANCEL
>;

export interface PayloadApiRequest<E extends EntityBase> extends AxiosRequestConfig {
  action?: AsyncAction<E, any>;
  resource?: ResourceLite<E>;
  meta?: Meta<E>;
  method?: 'get' | 'put' | 'post' | 'delete';
}

export default function* sagaApi<E extends EntityBase>(payload: PayloadApiRequest<E>) {
  const { action, meta = {}, ...config } = payload;
  const cancelSource = Axios.CancelToken.source();
  try {
    // Skip calls for resources NO-OP
    if (config.url === 'NO-OP') {
      if (action && action.cancel) {
        yield put(action.cancel(config));
      }
      return;
    }
    // NOTE: Each project will need to make sure that the client is instantiated prior to using it.
    const axiosClient = axiosClientService.get(meta.axiosClientName);

    // Show loading
    if (meta.showLoading) {
      yield put(updateLoading(true));
    }
    // Make the call
    const res = (yield call(axiosClient.request, {
      // NOTE: Serialize params using comma separated for arrays.
      // E.g.: `userId=1,2,3` instead of `userId[]=1&userId[]=2&userId[]=3`
      paramsSerializer: (params: Record<string, any>) => queryString.stringify(params),
      cancelToken: cancelSource.token,
      ...config,
    })) as AxiosResponse;
    // @ts-ignore
    return yield sagaHandleApiResponse<E>(res, payload);
  } catch (err) {
    // @ts-ignore
    return yield sagaHandleApiError(err, payload);
  } finally {
    // WORKAROUND cancellation works with `whatwg-fetch` but not with `axios`
    // https://stackoverflow.com/a/50086578
    // https://github.com/axios/axios#cancellation
    // @ts-ignore
    if (yield cancelled()) {
      cancelSource.cancel();
      if (action) {
        yield put(action.cancel('Action canceled during request'));
      }
    }
  }
}
